import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type { DateFilterQuery } from "@shared/list-filters";

type Mode = "specific" | "between";

export function DateFilter({
  label,
  value,
  onChange,
}: {
  label: string;
  value?: DateFilterQuery;
  onChange: (value: DateFilterQuery | undefined, mode: Mode) => void;
}) {
  const [mode, setMode] = useState<Mode>(value?.from ? "between" : "specific");
  const [exact, setExact] = useState(value?.exact ?? "");
  const [from, setFrom] = useState(value?.from ?? "");
  const [to, setTo] = useState(value?.to ?? "");

  useEffect(() => {
    setMode(value?.from ? "between" : "specific");
    setExact(value?.exact ?? "");
    setFrom(value?.from ?? "");
    setTo(value?.to ?? "");
  }, [value?.exact, value?.from, value?.to]);

  const switchMode = (next: Mode) => {
    if (next === mode) return;
    setMode(next);
    setExact("");
    setFrom("");
    setTo("");
    onChange(undefined, next);
  };
  const incompleteRange = mode === "between" && ((from && !to) || (!from && to));
  const invalidRange = mode === "between" && from && to && from > to;

  return (
    <fieldset className="space-y-2">
      <legend className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</legend>
      <div className="flex gap-1" role="group" aria-label={`${label} mode`}>
        <Button
          type="button"
          size="sm"
          variant={mode === "specific" ? "default" : "outline"}
          aria-pressed={mode === "specific"}
          onClick={() => switchMode("specific")}
        >
          Specific date
        </Button>
        <Button
          type="button"
          size="sm"
          variant={mode === "between" ? "default" : "outline"}
          aria-pressed={mode === "between"}
          onClick={() => switchMode("between")}
        >
          Between dates
        </Button>
      </div>
      {mode === "specific" ? (
        <Input
          type="date"
          aria-label={`${label} specific date`}
          value={exact}
          onChange={(event) => {
            const next = event.target.value;
            setExact(next);
            onChange(next ? { exact: next } : undefined, mode);
          }}
        />
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <Input
            type="date"
            aria-label={`${label} start date`}
            value={from}
            onChange={(event) => {
              const next = event.target.value;
              setFrom(next);
              onChange(next && to && next <= to ? { from: next, to } : undefined, mode);
            }}
          />
          <Input
            type="date"
            aria-label={`${label} end date`}
            min={from || undefined}
            value={to}
            onChange={(event) => {
              const next = event.target.value;
              setTo(next);
              onChange(from && next && from <= next ? { from, to: next } : undefined, mode);
            }}
          />
        </div>
      )}
      {incompleteRange || invalidRange ? (
        <p className="text-xs text-muted-foreground" role="status" aria-live="polite">
          {invalidRange ? "Start date must be on or before end date." : "Enter both dates to apply the range."}
        </p>
      ) : null}
    </fieldset>
  );
}
